import {
  Goal,
  goalsArchivedSectionID,
  goalsCompletedSectionID,
  ID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import { getArchivedGoalIds } from '../../derived-goal-state';
import { buildGoalsById } from '../../get-goal-life-area';
import { getPresentParentIds, isAlsoRoot } from '../get-present-parent-ids';
import { orderGoalTreeChildren } from './order-goal-tree-children';
import { BuildGoalListTreeOptions } from './types';

export const buildNoGroupingGoalListTree = (
  goals: Goal[],
  {
    hideArchived,
    hideCompleted,
    allGoals,
    links,
    goalChildrenByParentId,
    rootLink,
  }: BuildGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  const archivedIds = getArchivedGoalIds(allGoals, { links });
  const activeGoals: ID[] = [];
  const archivedGoals: ID[] = [];
  const completedGoals: ID[] = [];

  const goalMap = goals.reduce<Record<ID, Goal>>((acc, goal) => {
    acc[goal.id] = goal;
    return acc;
  }, {});

  const goalsById = buildGoalsById(goals);

  const goalChildrenMap = goals.reduce<Record<ID, ID[]>>((acc, goal) => {
    acc[goal.id] = acc[goal.id] ?? [];
    // A goal nests under EVERY parent present in the set; the tree list keys nodes by path.
    getPresentParentIds(goal, (id) => !!goalMap[id], {
      links,
      goalsById,
    }).forEach((parentId) => {
      acc[parentId] = [...(acc[parentId] ?? []), goal.id];
    });
    return acc;
  }, {});

  const treeItems: Record<ID, TaskListTreeItem> = {};

  goals.forEach((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return;
    }
    if (!hideArchived && goal.archivedAt) {
      archivedGoals.push(goal.id);
    }

    // A completed goal never sits inline among its live siblings; it moves to its own section.
    if (goal.completedAt && !hideCompleted) {
      completedGoals.push(goal.id);
    }
    if (!goal.completedAt) {
      activeGoals.push(goal.id);
    }

    const children =
      goalChildrenMap[goal.id]?.filter((id) => {
        const child = goalMap[id];
        return child && !child.archivedAt && !child.completedAt;
      }) ?? [];
    treeItems[goal.id] = {
      id: goal.id,
      name: goal.name,
      type: TaskListItemTypes.Goal,
      children,
      isFolder: !!children.length,
    };
  });

  // Archived through an ancestor the filter dropped — no parent left to nest under.
  activeGoals.forEach((goalId) => {
    const goal = goalMap[goalId];
    if (goal.archivedAt || !archivedIds.has(goalId)) {
      return;
    }
    if (
      !getPresentParentIds(goal, (id) => !!goalMap[id], { links, goalsById })
        .length
    ) {
      archivedGoals.push(goalId);
    }
  });

  if (completedGoals.length) {
    treeItems[goalsCompletedSectionID] = {
      id: goalsCompletedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: '',
      children: completedGoals,
      isFolder: true,
    };
  }

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: '',
      children: archivedGoals,
      isFolder: true,
    };
  }

  const isTreeItemPresent = (id: ID) => !!treeItems[id];
  const presentParentOptions = { links, goalsById };
  const rootGoalIds: ID[] = [];
  // A nested goal linked to the list's root (or, unrooted, missing a parent from the set) is ALSO a root, listed first.
  const alsoRootGoalIds: ID[] = [];

  activeGoals.forEach((goalId) => {
    if (!hideArchived && archivedIds.has(goalId)) {
      return;
    }
    const goal = goalMap[goalId];

    if (
      !getPresentParentIds(goal, isTreeItemPresent, presentParentOptions).length
    ) {
      rootGoalIds.push(goalId);
      return;
    }
    if (
      isAlsoRoot(goal, isTreeItemPresent, { ...presentParentOptions, rootLink })
    ) {
      alsoRootGoalIds.push(goalId);
    }
  });

  const rootChildren = [...alsoRootGoalIds, ...rootGoalIds];

  if (completedGoals.length) {
    rootChildren.push(goalsCompletedSectionID);
  }

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  orderGoalTreeChildren(treeItems, goalChildrenByParentId);

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
